import { bookingInquiryStrings } from '@data/bookingInquiry';
import { deliveryRequestStrings } from '@data/deliveryRequest';
import { instructionStrings } from '@data/instruction';
import { preAlertStrings } from '@data/preAlert';
import {
  BookingInquirySource,
  BookingInquiryStatus,
  TradeCondition,
} from '@models/next/BookingInquiryEnums';
import { BookingInquiryProfit } from '@models/next/BookingInquiryProfit';
import { Company } from '@models/next/Company';
import { CustomsStatusName, CustomsRequestStatusName } from '@models/next/CustomsRequestStatus';
import { DeliveryRequestStatusName } from '@models/next/DeliveryRequestStatus';
import { DOStatusName } from '@models/next/DOStatus';
import { RemoteFile } from '@models/next/FileType';
import { InstructionStatusName } from '@models/next/Instruction';
import { MoveOutStatusName } from '@models/next/MoveOutStatus';
import { PaginationType } from '@models/next/PaginationType';
import { PreAlertStatus } from '@models/next/PreAlertStatus';
import { ServiceName } from '@models/next/RoutePlan';
import { InvoiceStatus, PaymentSlipStatus } from '@models/next/StatusFlows';
import { AdminUser } from '@models/next/User';
import { rolesByCountry } from '@models/Role';
import { BaseUser } from '@models/User';
import { firstLeftEither, makeMessagesEitherRightIf } from '@utils/errors';
import { f } from '@utils/nextForms';
import { z } from 'zod';

export const PreAlertType = z.enum(['current', 'past_finished', 'past_canceled']);

export type PreAlertType = z.infer<typeof PreAlertType>;

export const PreAlertPathType = z.enum([...PreAlertType.options, 'all']);

export type PreAlertPathType = z.infer<typeof PreAlertPathType>;

export const PreAlertLedgerLockStatus = z.enum(['locked', 'not_locked', 'preparing']);

export type PreAlertLedgerLockStatus = z.infer<typeof PreAlertLedgerLockStatus>;

export const PreAlertFields = z.object({
  // Model fields
  id: z.number(),
  booking_inquiry: z.number(),
  vessel_schedule: z.number().nullable(),
  status: PreAlertStatus.array(),
  no: z.string(),
  customer_no: z.string().label(preAlertStrings.customer_no),
  refer_number: z.string().label(preAlertStrings.refer_number),
  carrier: z.string().label(preAlertStrings.carrier),
  shipper: z.string().label(preAlertStrings.shipper),
  consignee: z.string().label(preAlertStrings.consignee),
  house_bl_number: z.string().label(preAlertStrings.house_bl_number),
  quantity: z.number().nullable().label(preAlertStrings.quantity),
  packages: z.string(),
  shipping_mark: z.string().label(preAlertStrings.shipping_mark),
  commodity: z.string().label(preAlertStrings.commodity),
  gross_weight: f.decimal().label(preAlertStrings.gross_weight),
  measurement: f.decimal().label(preAlertStrings.measurement),
  payments_terms: TradeCondition,
  is_empty: z.boolean(),
  is_manually_locked: z.boolean(),
  is_ledger_locked: z.boolean(),
  job_code: z.string().label(preAlertStrings.job_code),
  // Field `has_pickups` is useless.
  // Field `do_status`, `move_out_status` are used in StatusFlow, so ignored here.

  do_less_no: z.string(),
  do_less_no_file: RemoteFile.nullable(),
  move_out_no: z.string(),
  move_out_no_file: RemoteFile.nullable(),
  sea_bl_file: RemoteFile.nullable(),
  received_move_out_signal: z.boolean(),
  updated_at: z.string(),
  created_at: z.string(),

  // Vessel Schedule fields

  // "" when vessel schedule does not exist
  pol: z.string(),
  // "" when vessel schedule does not exist
  pod: z.string(),
  etd: f.dateString().nullable().label(preAlertStrings.etd),
  eta: f.dateString().nullable().label(preAlertStrings.eta),
  atd: f.dateString().nullable(),
  ata: f.dateString().nullable(),
  vessel: z.string().nullable(),
  voyage: z.string().nullable(),

  // Computed fields

  // vessel_schedule.current_exchange_rate.rate
  exchange_rate: f.decimal().nullable(),
  // booking_inquiry.overseas_local_amount
  overseas_local_amount: f.decimal().nullable(),
  // booking_inquiry.trade_condition
  trade_condition: TradeCondition,
  minimum_etd: f.dateString().nullable(),
  profit: BookingInquiryProfit,
  // booking_inquiry.tracking
  transport_status: z.string(),
  // time of お届け完了
  delivery_date: z.string().nullish().label(preAlertStrings.delivery_date),
  customs_instruction_status: InstructionStatusName.nullish(),
  customs_status: CustomsStatusName.nullish(),
  customs_request_status: CustomsRequestStatusName.nullish(),
  delivery_instruction_status: InstructionStatusName.nullish(),
  d2d_no: z.string().label(preAlertStrings.d2dNo),
  member_salesperson: z.string().nullish().label(preAlertStrings.memberSalesperson),
  company_name: z.string().label(preAlertStrings.companyName),
});

export type PreAlertFields = z.infer<typeof PreAlertFields>;

export const DashboardPreAlertListItem = z.object({
  // Shared fields
  id: PreAlertFields.shape.id,
  no: PreAlertFields.shape.no,
  shipper: PreAlertFields.shape.shipper,
  consignee: PreAlertFields.shape.consignee,
  quantity: PreAlertFields.shape.quantity,
  gross_weight: PreAlertFields.shape.gross_weight,
  measurement: PreAlertFields.shape.measurement,
  is_empty: PreAlertFields.shape.is_empty,
  is_manually_locked: PreAlertFields.shape.is_manually_locked,
  created_at: PreAlertFields.shape.created_at,
  trade_condition: PreAlertFields.shape.trade_condition,
  transport_status: PreAlertFields.shape.transport_status,
  profit: PreAlertFields.shape.profit,
  delivery_date: PreAlertFields.shape.delivery_date,
  customs_instruction_status: PreAlertFields.shape.customs_instruction_status,
  customs_status: PreAlertFields.shape.customs_status,
  customs_request_status: PreAlertFields.shape.customs_request_status,
  delivery_instruction_status: PreAlertFields.shape.delivery_instruction_status,

  is_ledger_locked: PreAlertFields.shape.is_ledger_locked,

  pod: PreAlertFields.shape.pod,
  pol: PreAlertFields.shape.pol,
  eta: PreAlertFields.shape.eta,
  etd: PreAlertFields.shape.etd,
  vessel: PreAlertFields.shape.vessel,

  // Specific fields
  agency_name: z.string().nullish(),

  cancel_detail: z.string().nullish(),
  company_name: z.string().label(preAlertStrings.companyName),
  d2d_no: z.string().label(preAlertStrings.d2dNo),
  delivery_request: z
    .object({
      delivery_date: z.string(),
      pickup_date: f.dateString(),
    })
    .nullable(),
  delivery_request_status: DeliveryRequestStatusName.nullish(),
  do_status: DOStatusName.nullish(),
  move_out_status: MoveOutStatusName.nullish(),
  invoice_status: InvoiceStatus.nullish(),
  english_name: z.string(),
  owner: AdminUser.nullish(),
  owner_company: Company.nullish(),
  payment_slip_status: PaymentSlipStatus.nullish(),
  salesperson_china_last_name: z.string().nullish(),
  salesperson_customs_last_name: z.string().nullish(),
  salesperson_japan_last_name: z.string().nullish(),
  member_salesperson_last_name: z.string().nullish(),
  source: BookingInquirySource,
  user: AdminUser.nullish(),
  status: BookingInquiryStatus,
});

export type DashboardPreAlertListItem = z.infer<typeof DashboardPreAlertListItem>;

export const DashboardPreAlertQuery = z
  .object({
    agency_id: z.number(),
    before_departure: z.boolean(),
    company_name: PreAlertFields.shape.company_name,
    consignee: PreAlertFields.shape.consignee.label(bookingInquiryStrings.consignee),
    created_at_after: z.string(),
    created_at_before: z.string(),
    customs_instruction_status: PreAlertFields.shape.customs_instruction_status.array(),
    customs_status: z.enum([...CustomsStatusName.options, '-']),
    customs_request_status: z.enum([...CustomsRequestStatusName.options, '-']),
    d2d_no: PreAlertFields.shape.d2d_no,
    delivery_instruction_status: PreAlertFields.shape.delivery_instruction_status.array(),
    delivery_request_pickup_date_after: f.dateString().label(deliveryRequestStrings.pickupDate),
    delivery_request_pickup_date_before: f.dateString().label(deliveryRequestStrings.pickupDate),
    delivery_request_delivery_date_after: f.dateString().label(deliveryRequestStrings.deliveryDate),
    delivery_request_delivery_date_before: f
      .dateString()
      .label(deliveryRequestStrings.deliveryDate),
    delivery_request_status: z.enum([...DeliveryRequestStatusName.options, '-']),
    delivery_date_after: f.dateString().label(preAlertStrings.delivery_date),
    delivery_date_before: f.dateString().label(preAlertStrings.delivery_date),
    do_status: DOStatusName.array(),
    eta_after: PreAlertFields.shape.eta.unwrap(),
    eta_before: PreAlertFields.shape.eta.unwrap(),
    etd_after: PreAlertFields.shape.etd.unwrap(),
    etd_before: PreAlertFields.shape.etd.unwrap(),
    fullname: z.string(),
    has_pickups: z.boolean(),
    has_salesperson_china: z.boolean(),
    has_salesperson_customs: z.boolean(),
    has_salesperson_japan: z.boolean(),
    invoice_status: InvoiceStatus,
    is_empty: z.boolean(),
    member_no: z.string(),
    member_salesperson: PreAlertFields.shape.member_salesperson,
    member_salesperson_id: z.number(),
    move_out_status: MoveOutStatusName,
    ordering: z.string(),
    page: PaginationType.shape.page,
    page_size: PaginationType.shape.page_size,
    payment_slip_status: PaymentSlipStatus,
    pod: z.string().label(preAlertStrings.pod),
    pol: z.string().label(preAlertStrings.pol),
    delivery_instruction_preferred_delivery_date_after: f
      .dateString()
      .label(instructionStrings.preferredDeliveryDate),
    delivery_instruction_preferred_delivery_date_before: f
      .dateString()
      .label(instructionStrings.preferredDeliveryDate),
    quantity: PreAlertFields.shape.quantity.label(preAlertStrings.quantity_ja),
    salesperson_china: z.string().label(preAlertStrings.salespersonChina),
    salesperson_china_id: z.number(),
    salesperson_customs_id: z.number(),
    salesperson_japan: z.string().label(preAlertStrings.salespersonJapan),
    salesperson_japan_id: z.number(),
    service: ServiceName.array(),
    trade_condition: z.string(),
    transport_status: z.string(),
    type: z.array(PreAlertType),
    vessel: z.string(),
    ledger_lock_status: PreAlertLedgerLockStatus,
  })
  .partial();

export type DashboardPreAlertQuery = z.infer<typeof DashboardPreAlertQuery>;

export const PreAlertQuery = z
  .object({
    page: z.number(),
    d2d_no: PreAlertFields.shape.d2d_no,
    ordering: z.string(),
    vessel: PreAlertFields.shape.vessel,
    type: PreAlertType.array(),
  })
  .partial();

export type PreAlertQuery = z.infer<typeof PreAlertQuery>;

export const PreAlertSearch = PreAlertQuery.extend({
  checklist: z.array(z.string()),
});

export type PreAlertSearch = z.infer<typeof PreAlertSearch>;

export const PreAlertDetails = z.object({
  // Shared fields
  booking_inquiry: PreAlertFields.shape.booking_inquiry,
  vessel_schedule: PreAlertFields.shape.vessel_schedule,
  no: PreAlertFields.shape.no,
  customer_no: PreAlertFields.shape.customer_no,
  refer_number: PreAlertFields.shape.refer_number,
  carrier: PreAlertFields.shape.carrier,
  shipper: PreAlertFields.shape.shipper,
  consignee: PreAlertFields.shape.consignee,
  house_bl_number: PreAlertFields.shape.house_bl_number,
  quantity: PreAlertFields.shape.quantity,
  packages: PreAlertFields.shape.packages,
  shipping_mark: PreAlertFields.shape.shipping_mark,
  commodity: PreAlertFields.shape.commodity,
  gross_weight: PreAlertFields.shape.gross_weight,
  measurement: PreAlertFields.shape.measurement,
  payments_terms: PreAlertFields.shape.payments_terms,
  is_empty: PreAlertFields.shape.is_empty,
  is_manually_locked: PreAlertFields.shape.is_manually_locked,
  is_ledger_locked: PreAlertFields.shape.is_ledger_locked,
  job_code: PreAlertFields.shape.job_code,

  sea_bl_file: PreAlertFields.shape.sea_bl_file,

  pod: PreAlertFields.shape.pod,
  pol: PreAlertFields.shape.pol,
  etd: PreAlertFields.shape.etd,
  eta: PreAlertFields.shape.eta,
  atd: PreAlertFields.shape.atd,
  ata: PreAlertFields.shape.ata,
  vessel: PreAlertFields.shape.vessel,
  voyage: PreAlertFields.shape.voyage,

  created_at: PreAlertFields.shape.created_at,
  updated_at: PreAlertFields.shape.updated_at,

  exchange_rate: PreAlertFields.shape.exchange_rate,
  overseas_local_amount: PreAlertFields.shape.overseas_local_amount,
  minimum_etd: PreAlertFields.shape.minimum_etd,
  profit: PreAlertFields.shape.profit,

  // Specific fields
  sea_waybill_id: z.number().nullable(),
  arrival_notice_id: z.number().nullable(),
});

export type PreAlertDetails = z.infer<typeof PreAlertDetails>;

export const PreAlertListItem = z.object({
  // Shared fields
  id: PreAlertFields.shape.id,
  no: PreAlertFields.shape.no,
  customer_no: PreAlertFields.shape.customer_no,
  shipper: PreAlertFields.shape.shipper,
  consignee: PreAlertFields.shape.consignee,
  quantity: PreAlertFields.shape.quantity,
  shipping_mark: PreAlertFields.shape.shipping_mark,
  commodity: PreAlertFields.shape.commodity,
  gross_weight: PreAlertFields.shape.gross_weight,
  measurement: PreAlertFields.shape.measurement,
  is_empty: PreAlertFields.shape.is_empty,
  created_at: PreAlertFields.shape.created_at,

  pol: PreAlertFields.shape.pol,
  pod: PreAlertFields.shape.pod,
  etd: PreAlertFields.shape.etd,
  eta: PreAlertFields.shape.eta,
  vessel: PreAlertFields.shape.vessel,
  voyage: PreAlertFields.shape.voyage,
  transport_status: PreAlertFields.shape.transport_status,
  delivery_date: PreAlertFields.shape.delivery_date,
  d2d_no: PreAlertFields.shape.d2d_no,

  // Specific fields
  invoice_price: f.decimal(),
  status: BookingInquiryStatus,
});

export type PreAlertListItem = z.infer<typeof PreAlertListItem>;

export const PreAlertListItemPages = z.object({
  count: z.number(),
  results: PreAlertListItem.array(),
});

export const PreAlertSearchStatus = z.enum(['-', '作成済']);

export const DashboardPreAlertSearchType = DashboardPreAlertQuery.merge(
  z
    .object({
      status: PreAlertSearchStatus.label(preAlertStrings.preAlert),
      checklist: z.array(z.string()),
    })
    .partial(),
);

export type DashboardPreAlertSearchType = z.infer<typeof DashboardPreAlertSearchType>;

export const DashboardPreAlertSearchFieldName = DashboardPreAlertSearchType.keyof();

export type DashboardPreAlertSearchFieldName = z.infer<typeof DashboardPreAlertSearchFieldName>;

export const DashboardPreAlertListItemPages = z.object({
  count: z.number(),
  results: DashboardPreAlertListItem.array(),
});

export type DashboardPreAlertListItemPages = z.infer<typeof DashboardPreAlertListItemPages>;

export const PreAlertCount = z.object({
  count: z.number(),
});

export type PreAlertCount = z.TypeOf<typeof PreAlertCount>;

export const DashboardPatchPreAlert = z
  .object({
    do_less_no: PreAlertFields.shape.do_less_no,
    do_less_no_file: PreAlertFields.shape.do_less_no_file,
    do_status_name: DOStatusName,
    move_out_no_file: PreAlertFields.shape.move_out_no_file,
    move_out_status_name: MoveOutStatusName,
    is_ledger_locked: PreAlertFields.shape.is_ledger_locked,
    sea_bl_file: PreAlertFields.shape.sea_bl_file,
  })
  .partial();

export const mapPreAlertStatusValueToLable = (value: boolean) => (value ? '-' : '作成済');
export const mapPreAlertStatusLabelToValue = (label: '-' | '作成済') => label === '-';

export class PreAlertHelper {
  static canLockLedgerLock(user: BaseUser, v: Pick<PreAlertFields, 'profit' | 'is_ledger_locked'>) {
    return firstLeftEither(
      () =>
        makeMessagesEitherRightIf(rolesByCountry['jp'].includes(user.role as any), {
          messages: [preAlertStrings.errorNoAuthLockLedgerLock],
        }),
      () =>
        makeMessagesEitherRightIf(!!v.profit.invoice && !!v.profit.payment, {
          messages: [preAlertStrings.errorCannotLockLedgerLock],
        }),
    );
  }

  static canUnlockLedgerLock(user: BaseUser) {
    return firstLeftEither(() =>
      makeMessagesEitherRightIf(user.role === 'admin', {
        messages: [preAlertStrings.errorNoAuthUnlockLedgerLock],
      }),
    );
  }
}
